import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import {
  buildLegacyDriverName,
  formatMoney,
  getCurrentSettlementRange,
  resolveLegacyDriverName,
  toISODate,
} from "../utils/driverDeliveries";

type CurrentUserResponse = {
  ok?: boolean;
  user?: {
    id?: number | string;
    username?: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    legacyDriverName?: string;
    rol?: string;
  };
  error?: string;
};

type LegacyDriverRow = {
  fecha_colecta?: string;
  estado?: string;
  precio_chofer?: string | number;
  porcentaje_chofer?: string | number;
  [key: string]: unknown;
};

type LegacyDriverResponse = {
  ok?: boolean;
  result?: LegacyDriverRow[];
  error?: string;
};

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const cardsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "12px",
};

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "20px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  padding: "18px",
};

const featureCardStyle: React.CSSProperties = {
  ...cardStyle,
  minWidth: 0,
  minHeight: "154px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const featureTopStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
};

const featureIconStyle: React.CSSProperties = {
  width: "38px",
  height: "38px",
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  fontSize: "18px",
  flexShrink: 0,
};

const featureTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const featureTextStyle: React.CSSProperties = {
  margin: "10px 0 18px 0",
  fontSize: "13px",
  lineHeight: 1.45,
  color: "var(--text-secondary)",
};

const featureValueStyle: React.CSSProperties = {
  marginTop: "14px",
  fontSize: "clamp(20px, 2vw, 26px)",
  fontWeight: 900,
  color: "var(--text-primary)",
  letterSpacing: "-0.04em",
  overflowWrap: "anywhere",
};

const buttonStyle: React.CSSProperties = {
  minHeight: "38px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--primary)",
  color: "white",
  padding: "0 14px",
  fontWeight: 800,
  cursor: "pointer",
};

const chartCardStyle: React.CSSProperties = {
  ...cardStyle,
  padding: "18px",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "17px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "13px",
  lineHeight: 1.5,
};

const chartGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
  gap: "12px",
  alignItems: "end",
  marginTop: "18px",
  minHeight: "220px",
};

const barWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "9px",
  height: "220px",
  borderRadius: "14px",
  border: "none",
  background: "transparent",
  color: "inherit",
  padding: 0,
  font: "inherit",
  cursor: "pointer",
  transition: "transform 200ms ease, filter 200ms ease, background-color 200ms ease",
  transformOrigin: "bottom center",
};

const barStyle = (height: number): React.CSSProperties => ({
  width: "100%",
  maxWidth: "42px",
  minHeight: height > 0 ? "14px" : "6px",
  height: `${height}%`,
  borderRadius: "10px 10px 6px 6px",
  background:
    height > 0
      ? `linear-gradient(
          180deg,
          color-mix(in srgb, var(--primary) 18%, transparent) 0%,
          color-mix(in srgb, var(--primary) 5%, transparent) 100%
        )`
      : "transparent",
  border:
    height > 0
      ? "2px solid color-mix(in srgb, var(--primary) 78%, var(--border))"
      : "1px dashed var(--border)",
  boxShadow:
    height > 0
      ? "inset 0 0 18px color-mix(in srgb, var(--primary) 7%, transparent)"
      : "none",
  transition:
    "background 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
});

const barValueStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const barLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  fontWeight: 700,
};

const emptyStateStyle: React.CSSProperties = {
  padding: "32px 20px",
  borderRadius: "18px",
  border: "1px dashed var(--border)",
  textAlign: "center",
  color: "var(--text-secondary)",
  marginTop: "18px",
};

const futureSectionStyle: React.CSSProperties = {
  ...cardStyle,
  padding: "18px",
};

const futureGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "12px",
  marginTop: "16px",
};

const futureItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "14px 16px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
};

const futureItemContentStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: 0,
};

const futureTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const futureTextStyle: React.CSSProperties = {
  marginTop: "3px",
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const futureBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: "6px 10px",
  borderRadius: "999px",
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-secondary)",
  fontSize: "11px",
  fontWeight: 800,
};

const periodControlsStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "8px",
  marginTop: "12px",
};

const periodFieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "5px",
};

const periodLabelStyle: React.CSSProperties = {
  fontSize: "10px",
  fontWeight: 800,
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const periodInputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "38px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 10px",
  fontSize: "12px",
};

const periodNavigationStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "38px minmax(0, 1fr) 38px",
  gap: "8px",
  alignItems: "stretch",
  marginTop: "12px",
};

const periodArrowStyle = (
  disabled: boolean
): React.CSSProperties => ({
  minHeight: "38px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: disabled ? "var(--surface)" : "var(--surface-alt)",
  color: "var(--text-primary)",
  fontSize: "20px",
  fontWeight: 800,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.35 : 1,
  boxShadow: disabled ? "none" : "var(--shadow)",
  transition:
    "background-color 180ms ease, opacity 180ms ease, transform 180ms ease",
});

const periodRangeStyle: React.CSSProperties = {
  minHeight: "38px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  fontSize: "11px",
  fontWeight: 800,
  lineHeight: 1.3,
};

const shiftISODate = (iso: string, days: number): string => {
  const date = new Date(`${iso}T12:00:00`);

  date.setDate(date.getDate() + days);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return 0;

  const raw = value.trim();
  if (!raw) return 0;

  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  let normalized = raw;

  if (hasComma && hasDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalized = raw.replace(",", ".");
  } else if (hasDot) {
    normalized = /^\d+\.\d+$/.test(raw) ? raw : raw.replace(/\./g, "");
  }

  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeCommissionPercentage = (value: unknown): number => {
  const numeric = toNumber(value);

  if (!numeric) return 0;
  if (numeric > 0 && numeric <= 1) return numeric;
  if (numeric > 1) return numeric / 100;

  return 0;
};

const getNetAmount = (row: LegacyDriverRow): number => {
  const bruto = toNumber(row.precio_chofer);
  const commission = normalizeCommissionPercentage(row.porcentaje_chofer);
  return bruto - bruto * commission;
};

const toTitleCase = (value: string): string => {
  return value
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// LEGACY — heurístico por nombre, candidato a eliminación (ver misma nota
// en src/utils/driverDeliveries.ts). Reemplazado por el vínculo
// determinístico driver_data.lightdata_username <-> legacy.envios.cadete
// (server/legacy/driverLink/) vía GET /v2/drivers/me/legacy-envios. No se
// corta acá todavía porque requiere la misma migración de identidad
// (lightdata_username) corrida en el entorno real que sirve esta pantalla
// — cortar sin eso deja a choferes reales sin sus envíos.
const buildLegacyDriverNameCandidates = (
  rawName: string,
  normalizedName?: string
): string[] => {
  const original = rawName.trim();
  const compact = rawName.replace(/\s+/g, " ").trim();
  const normalized = normalizedName?.replace(/\s+/g, " ").trim() || "";
  const doubleSpaced = compact.replace(/ /g, "  ");

  return Array.from(
    new Set(
      [
        original,
        compact,
        normalized,
        toTitleCase(compact),
        compact.toLowerCase(),
        compact.toUpperCase(),
        doubleSpaced,
        doubleSpaced.toLowerCase(),
        doubleSpaced.toUpperCase(),
      ].filter(Boolean)
    )
  );
};

const HomeDriver = () => {
  const navigate = useNavigate();
  const { token, userId, username, firstName, lastName } = useAuth();

  const [driverFullName, setDriverFullName] = useState<string>(
    buildLegacyDriverName({
      username,
      first_name: firstName,
      last_name: lastName,
    }) || "Driver"
  );
  const [legacyDriverName, setLegacyDriverName] = useState<string>("");
  const [rows, setRows] = useState<LegacyDriverRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [weekOffset, setWeekOffset] = useState(0);
  const [periodMode, setPeriodMode] = useState<"settlement" | "custom">(
    "settlement"
  );

  const fetchDriverHome = async () => {
    if (!token || !userId || !username) {
      setError("No se pudo identificar el driver autenticado.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const userResponse = await authFetch(`/v2/users/${userId}`, { token });
      const userPayload = (await userResponse
        .json()
        .catch(() => ({}))) as CurrentUserResponse;

      const fallbackUser = {
        username,
        first_name: firstName,
        last_name: lastName,
      };

      const resolvedUser =
        userResponse.ok && userPayload?.user ? userPayload.user : fallbackUser;

      const resolvedFullName = buildLegacyDriverName(resolvedUser) || "Driver";
      const resolvedLegacyName = resolveLegacyDriverName(resolvedUser);
      const candidates = buildLegacyDriverNameCandidates(
        resolvedFullName,
        resolvedLegacyName
      );

      console.log("Legacy driver lookup:", {
        authContext: {
          userId,
          username,
          firstName,
          lastName,
        },
        userEndpointOk: userResponse.ok,
        userEndpointUser: userPayload?.user,
        resolvedFullName,
        resolvedLegacyName,
        candidates,
      });

      let matchedRows: LegacyDriverRow[] = [];
      let matchedName = candidates[0] || resolvedLegacyName;

      for (const candidate of candidates) {
        const response = await authFetch("/v1/data/driver/me", {
          method: "POST",
          token,
          json: true,
          body: JSON.stringify({
            username: candidate,
          }),
        });

        const payload = (await response
          .json()
          .catch(() => ({}))) as LegacyDriverResponse;

        if (!response.ok || payload?.ok === false) {
          if (response.status === 401) {
            throw new Error("Sesión inválida o vencida. Volvé a iniciar sesión.");
          }

          if (response.status === 403) {
            throw new Error(
              "Tu usuario no tiene permiso para ver el panel de driver."
            );
          }

          throw new Error(payload?.error || "No se pudieron cargar los envíos.");
        }

        const candidateRows = Array.isArray(payload.result)
          ? payload.result
          : [];

        if (candidateRows.length > 0) {
          matchedRows = candidateRows;
          matchedName = candidate;
          break;
        }
      }

      console.log("Legacy driver matched:", {
        matchedName,
        totalRows: matchedRows.length,
      });

      setDriverFullName(resolvedFullName);
      setLegacyDriverName(matchedName);
      setRows(matchedRows);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Error cargando panel driver.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverHome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId, username, firstName, lastName]);

  const availablePeriod = useMemo(() => {
    const dates = rows
      .map((row) => toISODate(safeString(row.fecha_colecta)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    return {
      from: dates[0] || "",
      to: dates[dates.length - 1] || "",
    };
  }, [rows]);

  const currentSettlementRange = useMemo(
    () => getCurrentSettlementRange(),
    []
  );

  const selectedSettlementRange = useMemo(() => {
    const daysToMove = weekOffset * 7;

    return {
      from: shiftISODate(currentSettlementRange.from, daysToMove),
      to: shiftISODate(currentSettlementRange.to, daysToMove),
    };
  }, [
    currentSettlementRange.from,
    currentSettlementRange.to,
    weekOffset,
  ]);

  useEffect(() => {
    if (periodMode !== "settlement") return;

    setDateFrom(selectedSettlementRange.from);
    setDateTo(selectedSettlementRange.to);
  }, [
    periodMode,
    selectedSettlementRange.from,
    selectedSettlementRange.to,
  ]);

  const handlePreviousSettlement = () => {
    setPeriodMode("settlement");

    setWeekOffset((currentOffset) => {
      if (periodMode === "custom") return -1;

      return currentOffset - 1;
    });
  };

  const handleNextSettlement = () => {
    if (periodMode === "settlement" && weekOffset >= 0) return;

    setPeriodMode("settlement");

    setWeekOffset((currentOffset) => {
      if (periodMode === "custom") return 0;

      return Math.min(0, currentOffset + 1);
    });
  };

  const isNextSettlementDisabled =
    loading || (periodMode === "settlement" && weekOffset >= 0);

  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      const iso = toISODate(safeString(row.fecha_colecta));

      if (!iso) return false;
      if (dateFrom && iso < dateFrom) return false;
      if (dateTo && iso > dateTo) return false;

      return true;
    });
  }, [rows, dateFrom, dateTo]);

  const summary = useMemo(() => {
    const total = visibleRows.length;
    const net = visibleRows.reduce(
      (acc, row) => acc + getNetAmount(row),
      0
    );

    return {
      total,
      net,
    };
  }, [visibleRows]);

  const chartData = useMemo(() => {
    const grouped = new Map<string, number>();

    visibleRows.forEach((row) => {
      const iso = toISODate(safeString(row.fecha_colecta));
      if (!iso) return;
      grouped.set(iso, (grouped.get(iso) || 0) + 1);
    });

    return Array.from(grouped.entries())
      .map(([iso, count]) => ({
        iso,
        label: iso.split("-").reverse().join("/"),
        count,
      }))
      .sort((a, b) => a.iso.localeCompare(b.iso));
  }, [visibleRows]);

  const formatPeriodDate = (iso: string): string => {
    if (!iso) return "";

    return iso.split("-").reverse().join("/");
  };

  const settlementPeriod = useMemo(() => {
    if (dateFrom && dateTo) {
      if (dateFrom === dateTo) {
        return formatPeriodDate(dateFrom);
      }

      return `${formatPeriodDate(dateFrom)} al ${formatPeriodDate(dateTo)}`;
    }

    if (dateFrom) {
      return `Desde ${formatPeriodDate(dateFrom)}`;
    }

    if (dateTo) {
      return `Hasta ${formatPeriodDate(dateTo)}`;
    }

    return "Sin período seleccionado";
  }, [dateFrom, dateTo]);

  const settlementNavigationLabel =
    periodMode === "custom"
      ? "Período personalizado"
      : weekOffset === 0
        ? `Liquidación actual · ${settlementPeriod}`
        : `Liquidación · ${settlementPeriod}`;

  const maxCount = Math.max(...chartData.map((item) => item.count), 1);

  const handleDownloadWeeklyDetail = async () => {
    if (!token || !legacyDriverName) return;

    try {
      setDownloading(true);

      const response = await authFetch("/v1/data/driver/print", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({
          username: legacyDriverName,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Sesión inválida o vencida. Volvé a iniciar sesión.");
        }

        if (response.status === 403) {
          throw new Error(
            "Tu usuario no tiene permiso para descargar el detalle semanal. Backend debe permitir DRIVER en /v1/data/driver/print."
          );
        }

        const text = await response.text();
        throw new Error(text || "No se pudo descargar el detalle semanal.");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = legacyDriverName.replace(/\s+/g, "_").toLowerCase();

      link.href = objectUrl;
      link.download = `detalle_semanal_${safeName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(err);
      alert(
        err instanceof Error
          ? err.message
          : "No se pudo descargar el detalle semanal."
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <section style={cardsGridStyle}>
        <div className="lynx-hover-panel" style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <h2 style={featureTitleStyle}>Neto estimado</h2>
              <div style={featureIconStyle}>◷</div>
            </div>

            <div style={featureValueStyle}>
              {loading ? "Cargando..." : `$ ${formatMoney(summary.net)}`}
            </div>
          </div>

          <button
            style={buttonStyle}
            onClick={handleDownloadWeeklyDetail}
            disabled={downloading || loading || !legacyDriverName}
          >
            {downloading ? "Descargando..." : "Descargar detalle"}
          </button>
        </div>

        <div className="lynx-hover-panel" style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <h2 style={featureTitleStyle}>Entregas reconocidas</h2>
              <div style={featureIconStyle}>✓</div>
            </div>

            <div style={featureValueStyle}>
              {loading ? "Cargando..." : summary.total}
            </div>

            <p style={featureTextStyle}>
              Registros incluidos en la liquidación cargada.
            </p>
          </div>
        </div>

        <div className="lynx-hover-panel" style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <h2 style={featureTitleStyle}>Período visualizado</h2>
              <div style={featureIconStyle}>◷</div>
            </div>

            <div style={featureValueStyle}>
              {loading ? "Cargando..." : settlementPeriod}
            </div>

            <div style={periodNavigationStyle}>
              <button
                type="button"
                className="lynx-hover-control"
                style={periodArrowStyle(loading)}
                onClick={handlePreviousSettlement}
                disabled={loading}
                aria-label="Ver liquidación anterior"
                title="Ver liquidación anterior"
              >
                ‹
              </button>

              <div style={periodRangeStyle}>
                {loading ? "Cargando..." : settlementNavigationLabel}
              </div>

              <button
                type="button"
                className="lynx-hover-control"
                style={periodArrowStyle(isNextSettlementDisabled)}
                onClick={handleNextSettlement}
                disabled={isNextSettlementDisabled}
                aria-label="Ver liquidación siguiente"
                title={
                  isNextSettlementDisabled
                    ? "Estás viendo la liquidación actual"
                    : "Ver liquidación siguiente"
                }
              >
                ›
              </button>
            </div>

            <div style={periodControlsStyle}>
              <label style={periodFieldStyle}>
                <span style={periodLabelStyle}>Desde</span>

                <input
                  type="date"
                  value={dateFrom}
                  min={availablePeriod.from || undefined}
                  max={dateTo || currentSettlementRange.to || undefined}
                  onChange={(event) => {
                    setPeriodMode("custom");
                    setDateFrom(event.target.value);
                  }}
                  style={periodInputStyle}
                  disabled={loading || !availablePeriod.from}
                />
              </label>

              <label style={periodFieldStyle}>
                <span style={periodLabelStyle}>Hasta</span>

                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || availablePeriod.from || undefined}
                  max={currentSettlementRange.to || undefined}
                  onChange={(event) => {
                    setPeriodMode("custom");
                    setDateTo(event.target.value);
                  }}
                  style={periodInputStyle}
                  disabled={loading || !availablePeriod.to}
                />
              </label>
            </div>
          </div>
        </div>

      </section>

      <section style={chartCardStyle}>
        <h2 style={sectionTitleStyle}>Entregas por fecha de colecta</h2>
        <p style={sectionTextStyle}>
          Cantidad de entregas reconocidas en cada fecha del período cargado.
        </p>

        {loading ? (
          <div style={emptyStateStyle}>Cargando información...</div>
        ) : error ? (
          <div style={emptyStateStyle}>{error}</div>
        ) : visibleRows.length === 0 ? (
          <div style={emptyStateStyle}>
            No hay entregas disponibles para el período cargado.{" "}
            <strong>{legacyDriverName || driverFullName}</strong>.
          </div>
        ) : (
          <div style={chartGridStyle}>
            {chartData.map((item) => {
              const height = Math.max(
                (item.count / maxCount) * 100,
                item.count > 0 ? 10 : 4
              );

              return (
                <button
                  type="button"
                  key={item.iso}
                  style={barWrapStyle}
                  title={`Ver ${item.count} entregas del ${item.label}`}
                  onClick={() => {
                    navigate(
                      `/driver/deliveries?date=${encodeURIComponent(item.iso)}`
                    );
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.transform =
                      "translateY(-4px) scale(1.025)";
                    event.currentTarget.style.filter =
                      "brightness(1.18) drop-shadow(0 8px 12px rgba(218, 166, 68, 0.16))";
                    event.currentTarget.style.background =
                      "color-mix(in srgb, var(--primary) 3%, transparent)";
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.transform =
                      "translateY(0) scale(1)";
                    event.currentTarget.style.filter = "brightness(1)";
                    event.currentTarget.style.background = "transparent";
                  }}
                >
                  <div style={barValueStyle}>{item.count}</div>
                  <div style={barStyle(height)} />
                  <div style={barLabelStyle}>{item.label}</div>
                </button>
              );
            })}
          </div>
        )}
      </section>
      <section style={futureSectionStyle}>
        <h2 style={sectionTitleStyle}>Próximas herramientas</h2>

        <p style={sectionTextStyle}>
          Funciones planificadas para ampliar la experiencia del chofer.
        </p>

        <div style={futureGridStyle}>
          <div className="lynx-hover-panel" style={futureItemStyle}>
            <div style={futureItemContentStyle}>
              <div style={featureIconStyle}>!</div>

              <div>
                <div style={futureTitleStyle}>Reclamos</div>
                <div style={futureTextStyle}>
                  Informar faltantes u observaciones operativas.
                </div>
              </div>
            </div>

            <span style={futureBadgeStyle}>Próximamente</span>
          </div>

          <div className="lynx-hover-panel" style={futureItemStyle}>
            <div style={futureItemContentStyle}>
              <div style={featureIconStyle}>🚀</div>

              <div>
                <div style={futureTitleStyle}>Envíos Turbo</div>
                <div style={futureTextStyle}>
                  Postularse para realizar entregas turbo.
                </div>
              </div>
            </div>

            <span style={futureBadgeStyle}>Próximamente</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeDriver;